import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext'; // Import the useAuth hook
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';

const LoginPage = ({ onNavigate }) => {
  const { login, isLoading: authLoading } = useAuth(); // Use the auth context

  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [locationStatus, setLocationStatus] = useState('Location access is optional for login.');
  const [locationGranted, setLocationGranted] = useState(false);
  const [showLocationButtons, setShowLocationButtons] = useState(true);
  const [userLocation, setUserLocation] = useState({ lat: null, lng: null });
  const [isMobile, setIsMobile] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    // Check if location was previously granted
    const wasLocationGranted = sessionStorage.getItem('locationGranted') === 'true';
    if (wasLocationGranted) {
      setLocationGranted(true);
      setLocationStatus('Location access granted ✓');
      setShowLocationButtons(false);
      const lat = sessionStorage.getItem('userLat');
      const lng = sessionStorage.getItem('userLng');
      if (lat && lng) {
        setUserLocation({ lat: parseFloat(lat), lng: parseFloat(lng) });
      }
    }

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Check if on internal network
  const isInternalNetwork = () => {
    const hostname = window.location.hostname;
    return hostname === 'localhost' ||
      hostname.startsWith('192.168.') ||
      hostname.startsWith('10.') ||
      hostname.startsWith('172.') ||
      hostname === '127.0.0.1';
  };

  // Handle location request
  const requestLocation = () => {
    setLocationStatus('Requesting location access...');
    setShowLocationButtons(false);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setUserLocation({ lat, lng });
          setLocationGranted(true);
          setLocationStatus('Location access granted ✓');

          sessionStorage.setItem('locationGranted', 'true');
          sessionStorage.setItem('userLat', lat.toString());
          sessionStorage.setItem('userLng', lng.toString());
        },
        (error) => {
          console.error('Location error:', error);
          setLocationStatus('Location access denied. You can still login.');
          setShowLocationButtons(false);
        }
      );
    } else {
      setLocationStatus('Geolocation not supported. You can still login.');
      setShowLocationButtons(false);
    }
  };

  // Skip location
  const skipLocation = () => {
    setLocationStatus('Location access skipped (optional)');
    setShowLocationButtons(false);
    sessionStorage.setItem('locationSkipped', 'true');
  };

  // Handle manual location for internal network
  const enableManualLocation = () => {
    const defaultLat = 22.5423261;
    const defaultLng = 88.3587712;

    setUserLocation({ lat: defaultLat, lng: defaultLng });
    setLocationGranted(true);
    setLocationStatus('Location access granted (Network Device) ✓');
    setShowLocationButtons(false);

    sessionStorage.setItem('locationGranted', 'true');
    sessionStorage.setItem('userLat', defaultLat.toString());
    sessionStorage.setItem('userLng', defaultLng.toString());
    sessionStorage.setItem('manualLocation', 'true');
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle login submission using AuthContext
  const handleLogin = async () => {
    if (!formData.username || !formData.password) {
      alert('Please enter both username and password');
      return;
    }

    setIsLoading(true);

    try {
      // Prepare location data if available
      let locationData = null;
      if (locationGranted && userLocation.lat && userLocation.lng) {
        locationData = {
          lat: userLocation.lat,
          lng: userLocation.lng,
          manualLocation: sessionStorage.getItem('manualLocation') === 'true'
        };
      }

      // Use AuthContext login function
      const result = await login(formData.username, formData.password, locationData);

      if (result.success) {
        // Navigate to appropriate dashboard based on role
        const role = result.user.role;
        const dashboardRoutes = {
          student: 'student-dashboard',
          teacher: 'teacher-dashboard',
          hod: 'hod-dashboard',
          admin: 'admin-dashboard',
          parent: 'parent-dashboard',
          accounts: 'accounts-dashboard',
          superadmin: 'superadmin-dashboard',
          vendor: 'vendor-dashboard',
          librarian: 'library-dashboard'
        };

        const dashboardRoute = dashboardRoutes[role];
        if (dashboardRoute) {
          onNavigate(dashboardRoute);
        } else {
          alert('Unknown user role');
        }
      } else {
        alert(result.message || 'Login failed');
      }

    } catch (error) {
      console.error('Login error:', error);
      alert('Login failed. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0fdfa 0%, #dcfce7 50%, #e6fffa 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background decoration similar to landing page */}
      <div style={{
        position: 'absolute',
        top: '-50px',
        right: '-50px',
        width: '200px',
        height: '200px',
        background: 'linear-gradient(45deg, rgba(15, 118, 110, 0.1), rgba(5, 150, 105, 0.1))',
        borderRadius: '50%',
        filter: 'blur(40px)'
      }}></div>

      <div style={{
        position: 'absolute',
        bottom: '-50px',
        left: '-50px',
        width: '150px',
        height: '150px',
        background: 'linear-gradient(45deg, rgba(15, 118, 110, 0.08), rgba(5, 150, 105, 0.08))',
        borderRadius: '50%',
        filter: 'blur(30px)'
      }}></div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '20px',
        position: 'relative'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '20px',
          padding: isMobile ? '2rem' : '3rem',
          width: '100%',
          maxWidth: '450px',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(15, 118, 110, 0.05)',
          border: '1px solid rgba(15, 118, 110, 0.1)',
          position: 'relative'
        }}>
          {/* Header Section */}
          <div style={{
            textAlign: 'center',
            marginBottom: '2rem'
          }}>
            {/* SVU Logo */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '1.5rem'
            }}>
              <img
                src="/logo.png"
                alt="Swami Vivekananda University Logo"
                style={{
                  width: '80px',
                  height: '80px',
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 4px 8px rgba(15, 118, 110, 0.1))'
                }}
              />
            </div>

            <h1 style={{
              fontSize: isMobile ? '24px' : '28px',
              fontWeight: 'bold',
              color: '#1f2937',
              marginBottom: '0.5rem',
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }}>Welcome Back</h1>

            <p style={{
              fontSize: '16px',
              color: '#6b7280',
              margin: 0
            }}>Sign in to your portal account</p>
          </div>

          {/* Location Status Section */}
          <div style={{
            backgroundColor: locationGranted ? '#f0fdf4' : '#f0fdfa',
            border: locationGranted ? '1px solid #bbf7d0' : '1px solid #a7f3d0',
            borderRadius: '12px',
            padding: '1rem',
            marginBottom: '1.5rem',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '14px',
              color: locationGranted ? '#166534' : '#0f766e',
              fontWeight: '500',
              marginBottom: showLocationButtons ? '0.75rem' : '0'
            }}>
              {locationStatus}
            </div>

            {showLocationButtons && (
              <div style={{
                display: 'flex',
                gap: '0.75rem',
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={requestLocation}
                  style={{
                    backgroundColor: '#0f766e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#0d9488'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#0f766e'}
                >
                  Allow Location
                </button>
                <button
                  onClick={skipLocation}
                  style={{
                    backgroundColor: 'transparent',
                    color: '#0f766e',
                    border: '1px solid #0f766e',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#0f766e';
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#0f766e';
                  }}
                >
                  Skip
                </button>
              </div>
            )}

            {isInternalNetwork() && !locationGranted && !showLocationButtons && (
              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                backgroundColor: '#fffbeb',
                border: '1px solid #fed7aa',
                borderRadius: '8px'
              }}>
                <p style={{
                  margin: '0 0 0.75rem 0',
                  fontSize: '13px',
                  color: '#92400e',
                  fontWeight: '500'
                }}>
                  Internal network detected: {window.location.hostname}
                </p>
                <button
                  onClick={enableManualLocation}
                  style={{
                    backgroundColor: '#0f766e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    width: '100%',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#0d9488'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#0f766e'}
                >
                  Enable Network Location
                </button>
              </div>
            )}
          </div>

          {/* Login Form */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              Username
            </label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                fontSize: '16px',
                transition: 'border-color 0.3s ease',
                outline: 'none',
                boxSizing: 'border-box',
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }}
              onFocus={(e) => e.target.style.borderColor = '#0f766e'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              placeholder="Enter your username"
            />
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                style={{
                  width: '100%',
                  padding: '12px 40px 12px 16px', // Added paddingRight for icon
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  fontSize: '16px',
                  transition: 'border-color 0.3s ease',
                  outline: 'none',
                  boxSizing: 'border-box',
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }}
                onFocus={(e) => e.target.style.borderColor = '#0f766e'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#0f766e',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'color 0.2s ease'
                }}
              >
                {showPassword ? (
                  <MdVisibility size={20} />
                ) : (
                  <MdVisibilityOff size={20} />
                )}
              </button>
            </div>
          </div>

          {/* Login Button */}
          <button
            onClick={handleLogin}
            disabled={isLoading || authLoading}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: (isLoading || authLoading) ? '#9ca3af' : '#0f766e',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: (isLoading || authLoading) ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              marginBottom: '1.5rem',
              boxShadow: '0 4px 12px rgba(15, 118, 110, 0.2)'
            }}
            onMouseEnter={(e) => {
              if (!isLoading && !authLoading) {
                e.target.style.backgroundColor = '#0d9488';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 6px 16px rgba(15, 118, 110, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading && !authLoading) {
                e.target.style.backgroundColor = '#0f766e';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(15, 118, 110, 0.2)';
              }
            }}
          >
            {(isLoading || authLoading) ? 'Signing In...' : 'Sign In'}
          </button>

          {/* Back to Home Button */}
          {onNavigate && (
            <button
              onClick={() => onNavigate('home')}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: 'transparent',
                color: '#0f766e',
                border: '2px solid #0f766e',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                marginBottom: '1.5rem'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#0f766e';
                e.target.style.color = 'white';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#0f766e';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              ← Back to Home
            </button>
          )}

          {/* Footer */}
          <div style={{
            textAlign: 'center',
            paddingTop: '1.5rem',
            borderTop: '1px solid #e5e7eb'
          }}>
            <p style={{
              fontSize: '12px',
              color: '#9ca3af',
              margin: 0
            }}>
              Developed by <span style={{ color: '#0f766e', fontWeight: '500' }}>LearningHub</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;